import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { logout } from '../../../store/slices/authSlice';
import { checkTokenExpiration, formatTime } from '../../../utils/jwt/jwtUtils';

interface SessionTimerProps {
  className?: string;
  warningThreshold?: number;
  dangerThreshold?: number;
}

const SessionTimer: React.FC<SessionTimerProps> = ({
  className,
  warningThreshold = 300,
  dangerThreshold = 60,
}) => {
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const handleTokenExpiration = async () => {
      try {
        await dispatch(logout());
        navigate('/admin/login', { replace: true });
      } catch (error) {
        navigate('/admin/login', { replace: true });
      }
    };

    const checkInitialToken = () => {
      const tokenInfo = checkTokenExpiration();
      if (!tokenInfo.hasToken || tokenInfo.isExpired) {
        setIsExpired(true);
        setRemainingTime(0);
        void handleTokenExpiration();
        return;
      }

      setRemainingTime(tokenInfo.remainingTime);
      setIsExpired(false);
    };

    checkInitialToken();

    const timer = setInterval(() => {
      const tokenInfo = checkTokenExpiration();

      if (!tokenInfo.hasToken) {
        setIsExpired(true);
        setRemainingTime(0);
        clearInterval(timer);
        void handleTokenExpiration();
        return;
      }

      if (tokenInfo.isExpired || tokenInfo.remainingTime <= 0) {
        setIsExpired(true);
        setRemainingTime(0);
        clearInterval(timer);
        void handleTokenExpiration();
        return;
      }

      setRemainingTime(tokenInfo.remainingTime);
      setIsExpired(false);
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [dispatch, navigate]);

  if (isExpired || remainingTime <= 0) {
    return null;
  }

  const isDanger = remainingTime <= dangerThreshold;
  const isWarning = !isDanger && remainingTime <= warningThreshold;

  return (
    <TimerRoot className={className}>
      <TimerText $isWarning={isWarning} $isDanger={isDanger}>
        관리자 세션 만료: {formatTime(remainingTime)}
      </TimerText>
    </TimerRoot>
  );
};

const TimerRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
`;

const TimerText = styled.span<{ $isWarning: boolean; $isDanger: boolean }>`
  color: #4481818d;
  font-size: 11px;
  font-family: var(--font-family-base);
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.02em;
  white-space: nowrap;

  ${({ $isWarning }) =>
    $isWarning &&
    css`
      color: #ff6b6b;
    `}

  ${({ $isDanger }) =>
    $isDanger &&
    css`
      color: #ff4757;
      font-weight: 600;
    `}

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

export default SessionTimer;
